import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Mic } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import PromptAudioRow from "@/components/PromptAudioRow";

export default function PromptAudioAdmin() {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/admin/prompt-audio").then((r) => setData(r.data)).catch(() => {}), []);
  const running = !!data?.job?.running;

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(load, 2000);
    return () => clearInterval(t);
  }, [running, load]);

  const recordAll = async () => {
    try {
      const r = await api.post("/admin/prompt-audio/generate-all");
      toast.success(r.data.queued ? `Recording ${r.data.queued} prompts in your voice…` : "Every prompt is already recorded.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start recording.");
    }
  };

  if (!data) return null;
  const sections = data.prompts.reduce((acc, p) => {
    (acc[p.section_title] ||= []).push(p);
    return acc;
  }, {});
  const job = data.job;

  return (
    <section className="border border-obsidian-600 p-6 mb-10" data-testid="prompt-audio-admin">
      <h2 className="font-serif text-2xl text-bone-100 mb-2">Workbook prompts in your voice</h2>
      <p className="font-sans text-sm text-bone-400 mb-4">
        Listeners who pick the Uncle Danny narrator hear these instantly, at no extra ElevenLabs cost. Record them all at once with your
        ElevenLabs voice, or upload your own take (mp3, wav, m4a) for any prompt.
      </p>
      <div className="flex items-center gap-4 flex-wrap mb-3">
        <Button onClick={recordAll} disabled={running} data-testid="prompt-audio-record-all"
          className="rounded-none uppercase tracking-wider text-xs bg-terracotta hover:bg-terracotta-deep text-obsidian-900">
          {running ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Mic className="h-4 w-4 mr-2" />} Pre-record all prompts in my voice
        </Button>
        <span className="font-sans text-sm text-bone-300" data-testid="prompt-audio-count">{data.recorded} of {data.total} recorded</span>
      </div>
      {job && (running || job.error) && (
        <p className={`font-sans text-sm mb-3 ${job.error ? "text-terracotta" : "text-bone-400"}`} data-testid="prompt-audio-job">
          {running ? `Recording… ${job.done + job.failed} of ${job.total}` : `Last run: ${job.done} recorded, ${job.failed} failed.`}
          {job.error && ` ${job.error}`}
        </p>
      )}
      {Object.entries(sections).map(([title, prompts]) => (
        <details key={title} className="border-t border-obsidian-600 py-2" data-testid={`prompt-audio-section-${prompts[0].section_id}`}>
          <summary className="cursor-pointer font-sans text-xs uppercase tracking-[0.22em] text-bone-300 py-2">
            {title} · {prompts.filter((p) => p.has_audio).length}/{prompts.length}
          </summary>
          <ul>{prompts.map((p) => <PromptAudioRow key={p.prompt_id} p={p} onChange={load} disabled={running} />)}</ul>
        </details>
      ))}
    </section>
  );
}
